<template>
  <b-container fluid class="">
    <h1 class="m-3">{{ title }}</h1>

    <b-row class="px-3 justify-content-between">
      <b-col cols="12">
        <div class="d-flex flex-wrap flex-lg-nowrap filter-gap">

          <div class="filter-width">
            <filter-site-picker :use-default-value="true" :multiple="true" :select-all="true" operator="equals"
              :item-key="siteIdFilterProperty" :item-label="siteLabelFilterProperty"
              :item-currency="siteCurrencyFilterProperty" :group-by-key="groupKey" :group-by-label="groupLabel"
              @filterWithIdChanged="siteFilterChanged($event)" />
          </div>

          <div class="d-flex filter-gap flex-wrap">
            <filter-picker-with-id ref="customFilterPicker" :title="$t('EventType.name')" :use-default-value="true"
              :multiple="true" :select-all="true" operator="equals" :item-key="eventTypeIdFilterProperty"
              :item-label="eventTypeLabelFilterProperty" :group-by-key="groupKeyEventType"
              :group-by-label="groupLabelEventType" :filterBySite="true"
              @filterWithIdChanged="eventTypeFilterChanged($event)" />

            <date-range-picker :allow-future-date="true" :date-picker-format="datePickerFormat"
              :date-range-to-force="dateRangeToForce" :use-stored-date-range="true" :date-dimension="dateDimension"
              @dateRangeFilterUpdated="dateRangeFilterUpdated($event)" />
          </div>

          <b-dropdown toggle-class="dropdown-style" right :text="$t('export.export')"
            class="ml-lg-auto align-self-center align-self-lg-start">
            <b-dropdown-item class="mb-2" :disabled="!dataReadyTables" @click="downloadPdf()">
              <font-awesome-icon icon="file-pdf" class="mr-2 export-icon-pdf" />
              {{ $t("export.PDF") }}
            </b-dropdown-item>
            <b-dropdown-item class="mb-2" :disabled="!dataReadyTables" @click="downloadExcel()">
              <font-awesome-icon icon="file-excel" class="mr-2 export-icon-excel" />
              {{ $t("export.excel") }}
            </b-dropdown-item>
            <b-dropdown-item :disabled="!dataReadyTables" @click="downloadCsv()">
              <font-awesome-icon icon="file-csv" class="mr-2 export-icon" />
              {{ $t("export.csv") }}
            </b-dropdown-item>
          </b-dropdown>

        </div>
      </b-col>
    </b-row>

    <table-renderer v-for="data of tablesData" :key="data.tableNumber" :data-table="data.dataTable" ref="tableRenderers"
      :dimension-fields="data.dimensionColumns" :measure-fields="data.measureColumns" :table-header="data.tableHeader"
      :group-headers="groupHeaders" :top-group-headers="topGroupHeaders" :default-sorted-column="dateDimension"
      :totals-data="data.totalsData" :data-ready="dataReadyTables" :custom-table-class="customTableClass" />
  </b-container>
</template>

<script>
import i18n from "@/plugins/vue-i18n.js";
import TableRenderer from "@/components/TableRenderer.vue";
import FilterPickerWithId from "@/components/picker/FilterPickerWithId.vue";
import FilterSitePicker from "@/components/picker/FilterSitePicker.vue";
import DateRangePicker from "@/components/picker/DateRangePicker.vue";
import TableTools from "@/utils/tableTools";
import paths from "@/router/pathConstants";
import dimensionEnum from "@/model/dimensionEnum";
import mesureEnum from "@/model/mesureEnum";
import Tools from "@/utils/tools";
import { calculatedColumnsEnum } from "@/model/columnsEnums";
import { columnTypeEnum } from "@/model/columnsEnums";
import { dateFormatEnum, dateFormat } from "@/filter/dateFormat";
import _ from "lodash";
import abortMixin from "@/mixins/abortMixin";

export default {
  name: "BusinessOmniplayEventsCore",
  mixins: [abortMixin],
  components: {
    FilterPickerWithId,
    FilterSitePicker,
    DateRangePicker,
    TableRenderer,
  },
  props: {
    /** Préfixe de l'identifiant de la table pour les exports ayant plusieurs groupes de tableaux */
    dateDimension: {
      type: String,
      default() {
        return dimensionEnum.OMNIPLAYEVENT_EVENTBUSINESSDATE;
      },
    },
  },
  data() {
    return {
      siteIdFilterProperty: dimensionEnum.SITE,
      siteLabelFilterProperty: dimensionEnum.SITE_NAME,
      siteCurrencyFilterProperty: dimensionEnum.SITE_CURRENCY,
      groupKey: dimensionEnum.SITE_SITE_CATEGORY,
      groupLabel: dimensionEnum.SITE_SITE_CATEGORY_NAME,
      eventTypeIdFilterProperty: dimensionEnum.EVENTTYPE,
      eventTypeLabelFilterProperty: dimensionEnum.EVENTTYPE_NAME,
      groupKeyEventType: dimensionEnum.EVENTTYPE_CATEGORY,
      groupLabelEventType: dimensionEnum.EVENTTYPE_CATEGORY_NAME,
      /** Libellé des dates sélectionnées, telles qu'affichées dans le DateRangePicker */
      dateRangeStr: "",
      /** Libellé de la période (dates) sélectionnée */
      labelDatePeriod: "",
      /** Libellé des horaires sélectionnés */
      labelTimeRange: "",
      /** Tableau d'objets des sites sélectionnés, indiquant leur id et leur label */
      siteValues: [],
      eventTypeValues: [],
      /** Filtre venant du FilterPicker, tel qu'attendu par cubeJs.
       * Ex :
       *    {
       *      dimension: "Site.label",
       *      operator: "equals",
       *      values: ["Site 1"]
       *    }
       */
      siteFilter: null,
      eventTypeFilter: null,
      /** Filtre venant du DateRangePicker, tel qu'attendu par cubeJs.
       * Ex :
       *    {
       *      dimension: "BusinessDate.id",
       *      operator: "inDateRange",
       *      values: ["2020-09-23", "2020-09-26"]
       *    }
       */
      dateRangeFilter: null,

      dateRangeToForce: [],
      datePickerFormat: dateFormatEnum.DATE_SHORT_FORMAT,
      filenamePrefix: "",

      separator: "##",
      total: this.$t(calculatedColumnsEnum.TOTAL_DATA),
      dataReadyTables: false,
      dimensionColumnsTables: [],
      measureColumnsTables: [],
      groupHeaders: [],
      topGroupHeaders: [],

      tablesData: [],
    };
  },

  mounted() {
    const emptyEvent = "Event";
    const emptyFormula = "Formule"
    const emptyDimension = [dimensionEnum.OMNIPLAYEVENT_EVENTBUSINESSDATE];
    const emptyMesure = [
      `${emptyEvent}${this.separator}${emptyFormula}${this.separator}${calculatedColumnsEnum.REVENUE_HT}`,
      `${emptyEvent}${this.separator}${emptyFormula}${this.separator}${calculatedColumnsEnum.REVENUE_TTC}`,
      `${emptyEvent}${this.separator}${emptyFormula}${this.separator}${mesureEnum.OMNIPLAYEVENT_NBCARDS}`,
      `${emptyEvent}${this.separator}${this.total}${this.separator}${calculatedColumnsEnum.REVENUE_HT}`,
      `${emptyEvent}${this.separator}${this.total}${this.separator}${calculatedColumnsEnum.REVENUE_TTC}`,
      `${emptyEvent}${this.separator}${this.total}${this.separator}${mesureEnum.OMNIPLAYEVENT_NBCARDS}`,
      `${this.total}${this.separator}${this.total}${this.separator}${calculatedColumnsEnum.REVENUE_HT}`,
      `${this.total}${this.separator}${this.total}${this.separator}${calculatedColumnsEnum.REVENUE_TTC}`,
      `${this.total}${this.separator}${this.total}${this.separator}${mesureEnum.OMNIPLAYEVENT_NBCARDS}`
    ];
    this.groupHeaders = this.getSubGroupHeaders(emptyMesure);
    this.topGroupHeaders = this.getTopGroupHeaders(emptyMesure);
    this.initDisplayTables(emptyMesure, emptyDimension);
    this.dataReadyTables = true;
    this.filenamePrefix = this.title;
  },
  computed: {
    title() {
      return `${this.$t("title.omniplay_event")} : ${this.$t(this.dateDimension)}`;
    },
    isDefaultQueryReady() {
      return (
        this.siteFilter &&
        this.dateRangeFilter &&
        (!this.eventTypeFilter || this.eventTypeFilter)
      );
    },
    customTableClass() {
      return "table-width-xl export-xxl";
    },
  },
  methods: {
    siteFilterChanged(value) {
      this.siteValues = value.values;
      this.siteFilter = value.idFilter;

      this.$refs.customFilterPicker.$emit("sites-updated");

      // Appeler filterUpdated au cas où eventTypeFilterChanged ne serait pas déclenché
      // ou pour mettre à jour immédiatement si les filtres sont déjà prêts
      this.filterUpdated();
    },
    eventTypeFilterChanged(value) {
      this.eventTypeValues = value.values;
      this.eventTypeFilter = value.idFilter;
      this.filterUpdated();
    },
    dateRangeFilterUpdated(value) {
      this.dateRangeFilter = value.dateRangeFilter || null;
      this.dateRangeStr = value.dateRangeStr || "";
      this.labelDatePeriod = value.labelPeriod || "";
      this.filterUpdated();
    },
    filterUpdated() {
      if (this.isDefaultQueryReady) {
        let filters = [this.siteFilter];

        if (this.eventTypeFilter) {
          filters.push(this.eventTypeFilter);
        }

        this.updateTableList(filters, this.dateRangeFilter);
      }
    },
    downloadPdf() {
      const data = this.assembleDownloadData();
      if (this.dataReadyTables) {
        this.storeExportData(data.filename, data.subtitles);
        window.open(paths.EXPORT_PDF, "_blank");
      }
    },
    downloadExcel() {
      const data = this.assembleDownloadData();
      if (this.dataReadyTables) {
        this.storeExportData(data.filename, data.subtitles);
        window.open(paths.EXPORT_EXCEL, "_blank");
      }
    },
    downloadCsv() {
      const data = this.assembleDownloadData();
      if (this.dataReadyTables) {
        this.storeExportData(data.filename, data.subtitles);
        window.open(paths.EXPORT_CSV, "_blank");
      }
    },
    assembleDownloadData() {
      return {
        filename: Tools.StringHelper.constructExportFilename(
          this.filenamePrefix,
          null,
          this.dateRangeStr
        ),
        subtitles: Tools.StringHelper.constructExportSubtitles(
          this.siteValues.map((v) => v.label),
          this.labelDatePeriod,
          this.labelTimeRange
        ),
      };
    },
    async updateTableList(filters, dateRangeFilter) {
      try {
        const { signal } = this.resetAbortController();
        var self = this;
        this.dataReadyTables = false;

        // Récupération des objets n'ayant pas été annulés
        filters.push({
          dimension: dimensionEnum.OMNIPLAYEVENT_ISCANCELLED,
          operator: "equals",
          values: ["0"],
        });

        // Initialisation des objets contenant toutes les données pour chaque site :
        // permet d'afficher dès maintenant les entêtes de tableaux et le loader pour indiquer que les requêtes sont en cours
        this.tablesData = this.siteValues.map((site, i) => {
          return {
            tableNumber: i.toString(),
            tableHeader: site.label,
            tableHeaderId: site.id,
            dataTable: [],
            totalsData: {},
          };
        });
        let measures = [
          mesureEnum.OMNIPLAYEVENT_TOTALHT,
          mesureEnum.OMNIPLAYEVENT_TOTALTTC,
          mesureEnum.OMNIPLAYEVENT_NBCARDS,
        ];
        let dimensions = [
          dimensionEnum.OMNIPLAYEVENT_FORMULA,
          dimensionEnum.SITE,
          this.dateDimension,
          dimensionEnum.OMNIPLAYEVENT_EVENTTYPE,
          dimensionEnum.FORMULA_TITLE,
          dimensionEnum.EVENTTYPE_NAME,
          dimensionEnum.SITE_CURRENCY,
        ];
        let query = {
          measures: measures,
          dimensions: dimensions,
          timeDimensions: dateRangeFilter ? [dateRangeFilter] : [],
          filters: filters,
        };

        let results = await TableTools.Helper.doRequest(query, { signal });

        // Extraire les devises par site depuis les résultats
        const siteCurrencies = {};
        results.forEach(result => {
          const siteId = result[dimensionEnum.SITE];
          if (siteId && result[dimensionEnum.SITE_CURRENCY] && !siteCurrencies[siteId]) {
            siteCurrencies[siteId] = result[dimensionEnum.SITE_CURRENCY];
          }
        });

        // Assigner les devises aux tables
        self.tablesData.forEach(table => {
          table.siteCurrency = siteCurrencies[table.tableHeaderId] || 'EUR';
        });

        if (results && results.length) {

          let resultSet = [];
          results.forEach((result) => {
            const formulaName = result[dimensionEnum.FORMULA_TITLE]
              ? result[dimensionEnum.FORMULA_TITLE]
              : result[dimensionEnum.OMNIPLAYEVENT_FORMULA];

            const eventTypeName = result[dimensionEnum.EVENTTYPE_NAME]
              ? result[dimensionEnum.EVENTTYPE_NAME]
              : result[dimensionEnum.OMNIPLAYEVENT_EVENTTYPE];

            result.groupByKey = eventTypeName + this.separator + formulaName;
          });
          let resultsBySite = _.groupBy(results, dimensionEnum.SITE);
          for (const site in resultsBySite) {
            let resultsBySiteByDay = _.groupBy(
              resultsBySite[site],
              this.dateDimension
            );
            let resultsOfTheDayBySite = [];
            for (let eventDate in resultsBySiteByDay) {
              let resultOfTheDayBySite = {};
              let totalTTC = 0;
              let totalHT = 0;
              let totalNbCards = 0;
              resultOfTheDayBySite[this.dateDimension] = eventDate;
              resultOfTheDayBySite[dimensionEnum.SITE] = site;
              // Première passe de récupération des résultats par Event et Par formule
              let eventsOfTheDay = _.groupBy(
                resultsBySiteByDay[eventDate],
                "groupByKey"
              );
              for (const key in eventsOfTheDay) {
                let amountHT = 0;
                let amountTTC = 0;
                let nbCards = 0;
                eventsOfTheDay[key].forEach((evt) => {
                  amountHT += evt[mesureEnum.OMNIPLAYEVENT_TOTALHT];
                  amountTTC += evt[mesureEnum.OMNIPLAYEVENT_TOTALTTC];
                  nbCards += evt[mesureEnum.OMNIPLAYEVENT_NBCARDS];
                });
                resultOfTheDayBySite[
                  key + this.separator + calculatedColumnsEnum.REVENUE_HT
                ] = amountHT;
                resultOfTheDayBySite[
                  key + this.separator + calculatedColumnsEnum.REVENUE_TTC
                ] = amountTTC;
                resultOfTheDayBySite[
                  key + this.separator + dimensionEnum.OMNIPLAYEVENT_NBCARDS
                ] = nbCards;
              }

              // Seconde passe de récupération des totaux par eventType
              eventsOfTheDay = _.groupBy(
                resultsBySiteByDay[eventDate],
                dimensionEnum.EVENTTYPE_NAME
              );
              for (const key in eventsOfTheDay) {
                let amountHT = 0;
                let amountTTC = 0;
                let nbCards = 0;
                eventsOfTheDay[key].forEach((evt) => {
                  amountHT += evt[mesureEnum.OMNIPLAYEVENT_TOTALHT];
                  amountTTC += evt[mesureEnum.OMNIPLAYEVENT_TOTALTTC];
                  nbCards += evt[mesureEnum.OMNIPLAYEVENT_NBCARDS];
                });
                resultOfTheDayBySite[
                  key +
                  this.separator +
                  this.total +
                  this.separator +
                  calculatedColumnsEnum.REVENUE_HT
                ] = amountHT;
                resultOfTheDayBySite[
                  key +
                  this.separator +
                  this.total +
                  this.separator +
                  calculatedColumnsEnum.REVENUE_TTC
                ] = amountTTC;
                resultOfTheDayBySite[
                  key +
                  this.separator +
                  this.total +
                  this.separator +
                  dimensionEnum.OMNIPLAYEVENT_NBCARDS
                ] = nbCards;
                totalHT += amountHT;
                totalTTC += amountTTC;
                totalNbCards += nbCards;
              }
              resultOfTheDayBySite[
                this.total +
                this.separator +
                this.total +
                this.separator +
                calculatedColumnsEnum.REVENUE_HT
              ] = totalHT;
              resultOfTheDayBySite[
                this.total +
                this.separator +
                this.total +
                this.separator +
                calculatedColumnsEnum.REVENUE_TTC
              ] = totalTTC;
              resultOfTheDayBySite[
                this.total +
                this.separator +
                this.total +
                this.separator +
                dimensionEnum.OMNIPLAYEVENT_NBCARDS
              ] = totalNbCards;

              resultsOfTheDayBySite.push(resultOfTheDayBySite);
              resultSet.push(resultOfTheDayBySite);
            }
          }

          // Extraction des colonnes
          let columns = [];
          resultSet.forEach((row) => {
            let col = Object.keys(row);
            columns = [...new Set([...columns, ...col])];
          });

          const index = columns.indexOf(dimensionEnum.SITE);
          columns.splice(index, 1);
          let measureColumns = this.reorganizeColumns(columns);
          let dimensionColumns = [this.dateDimension];
          this.groupHeaders = this.getSubGroupHeaders(measureColumns);
          this.topGroupHeaders = this.getTopGroupHeaders(measureColumns);

          self.siteValues.forEach((site) => {
            var table = self.tablesData.find(
              (tab) => tab.tableHeaderId === site.id
            );
            const tableSiteCurrency = table.siteCurrency || 'EUR';

            table.dataTable = resultSet.filter(
              (result) => result[dimensionEnum.SITE] === site.id
            );
            // Ajouter la devise à chaque ligne pour le formatage des totaux
            table.dataTable.forEach(row => {
              row["Site.currency"] = tableSiteCurrency;
            });

            // Créer les colonnes spécifiques pour ce tableau avec sa devise
            table.measureColumns = this.constructColumnTable(
              measureColumns,
              columnTypeEnum.MEASURE,
              tableSiteCurrency
            );
            table.dimensionColumns = this.constructColumnTable(
              dimensionColumns,
              columnTypeEnum.DIMENSION,
              tableSiteCurrency
            );

            table.totalsData = TableTools.Helper.calculateTotalsData(
              table.dataTable,
              measureColumns
            );
          });
        } else {
          // Pas de résultats, garder la structure par défaut des colonnes
          const emptyEvent = "Event";
          const emptyFormula = "Formule"
          const emptyDimension = [this.dateDimension];
          const emptyMesure = [
            `${emptyEvent}${this.separator}${emptyFormula}${this.separator}${calculatedColumnsEnum.REVENUE_HT} `,
            `${emptyEvent}${this.separator}${emptyFormula}${this.separator}${calculatedColumnsEnum.REVENUE_TTC} `,
            `${emptyEvent}${this.separator}${emptyFormula}${this.separator}${mesureEnum.OMNIPLAYEVENT_NBCARDS} `,
            `${emptyEvent}${this.separator}${this.total}${this.separator}${calculatedColumnsEnum.REVENUE_HT} `,
            `${emptyEvent}${this.separator}${this.total}${this.separator}${calculatedColumnsEnum.REVENUE_TTC} `,
            `${emptyEvent}${this.separator}${this.total}${this.separator}${mesureEnum.OMNIPLAYEVENT_NBCARDS} `,
            `${this.total}${this.separator}${this.total}${this.separator}${calculatedColumnsEnum.REVENUE_HT} `,
            `${this.total}${this.separator}${this.total}${this.separator}${calculatedColumnsEnum.REVENUE_TTC} `,
            `${this.total}${this.separator}${this.total}${this.separator}${mesureEnum.OMNIPLAYEVENT_NBCARDS} `
          ];
          this.groupHeaders = this.getSubGroupHeaders(emptyMesure);
          this.topGroupHeaders = this.getTopGroupHeaders(emptyMesure);

          // Créer les colonnes pour chaque tableau
          self.siteValues.forEach((site) => {
            var table = self.tablesData.find(
              (tab) => tab.tableHeaderId === site.id
            );
            if (table) {
              const tableSiteCurrency = table.siteCurrency || 'EUR';
              table.measureColumns = this.constructColumnTable(
                emptyMesure,
                columnTypeEnum.MEASURE,
                tableSiteCurrency
              );
              table.dimensionColumns = this.constructColumnTable(
                emptyDimension,
                columnTypeEnum.DIMENSION,
                tableSiteCurrency
              );
              table.dataTable = [];
              table.totalsData = {};
            }
          });
        }
        this.dataReadyTables = true;
      } catch (error) {
        if (this.isAbortError(error)) return;
        Tools.Alerts.Error(this.$swal, error);
      }
    },
    getSubGroupHeaders(measureColumns) {
      let groupHeaders = [];
      let groupedByPK = _.groupBy(
        measureColumns,
        (item) => _.split(item, this.separator)[0]
      );
      for (const pk in groupedByPK) {
        let groupedBySK = _.groupBy(
          groupedByPK[pk],
          (item) => _.split(item, this.separator)[1]
        );

        for (const sk in groupedBySK) {
          groupHeaders.push({
            nbOfMeasures: groupedBySK[sk].length,
            label: sk,
          });
        }
      }

      return groupHeaders;
    },
    getTopGroupHeaders(measureColumns) {
      let groupHeaders = [];
      let groupedByPK = _.groupBy(
        measureColumns,
        (item) => _.split(item, this.separator)[0]
      );
      for (const pk in groupedByPK) {
        groupHeaders.push({ nbOfMeasures: groupedByPK[pk].length, label: pk });
      }

      return groupHeaders;
    },
    initDisplayTables(measureColumns, dimensionColumns, siteCurrency = 'EUR') {
      const self = this;

      //Initialisation des colonnes affichées
      self.measureColumnsTables = this.constructColumnTable(
        measureColumns,
        columnTypeEnum.MEASURE,
        siteCurrency
      );
      this.dimensionColumnsTables = this.constructColumnTable(
        dimensionColumns,
        columnTypeEnum.DIMENSION,
        siteCurrency
      );
    },
    constructColumnTable(fullKeys, type, siteCurrency = 'EUR') {
      var columnTable = fullKeys.map((fullKey) => {
        let keySplitted = fullKey.split(this.separator)[2];
        if (keySplitted === undefined) {
          keySplitted = fullKey;
        }

        var column = {
          key: fullKey,
          label: i18n.t(keySplitted),
          type: type,
          formatter: (value) => {
            return TableTools.Helper.valueFormatter(value, keySplitted, false, siteCurrency);
          },
          tdClass: (value, key) => {
            return TableTools.Helper.cellStyleFormatter(value, key)
          },
          thAttr: (_value, key, _item) => {
            // ajout d'un tooltip sur le header si besoin
            return TableTools.Helper.addHeaderTooltip(key)
          }
        };
        return column;
      });
      return columnTable;
    },
    reorganizeColumns(columns) {
      const index = columns.indexOf(this.dateDimension);
      columns.splice(index, 1);
      let reorganizedColumns = [];
      let groupedByPK = _.groupBy(
        columns,
        (item) => _.split(item, this.separator)[0]
      );
      for (const pk in groupedByPK) {
        if (pk !== this.total) {
          let groupedBySK = _.groupBy(
            groupedByPK[pk],
            (item) => _.split(item, this.separator)[1]
          );
          for (const sk in groupedBySK) {
            if (sk !== this.total) {
              const revenueHTDimension = groupedBySK[sk].find(
                (x) =>
                  x.split(this.separator)[2] ===
                  calculatedColumnsEnum.REVENUE_HT
              );
              const revenueTTCDimension = groupedBySK[sk].find(
                (x) =>
                  x.split(this.separator)[2] ===
                  calculatedColumnsEnum.REVENUE_TTC
              );
              const nbCardsDimension = groupedBySK[sk].find(
                (x) =>
                  x.split(this.separator)[2] ===
                  dimensionEnum.OMNIPLAYEVENT_NBCARDS
              );
              reorganizedColumns.push(
                revenueHTDimension,
                revenueTTCDimension,
                nbCardsDimension
              );
            }
          }

          const revenueHTDimension = groupedBySK[this.total].find(
            (x) =>
              x.split(this.separator)[0] === pk &&
              x.split(this.separator)[2] === calculatedColumnsEnum.REVENUE_HT
          );
          const revenueTTCDimension = groupedBySK[this.total].find(
            (x) =>
              x.split(this.separator)[0] === pk &&
              x.split(this.separator)[2] === calculatedColumnsEnum.REVENUE_TTC
          );
          const nbCardsDimension = groupedBySK[this.total].find(
            (x) =>
              x.split(this.separator)[0] === pk &&
              x.split(this.separator)[2] === dimensionEnum.OMNIPLAYEVENT_NBCARDS
          );
          reorganizedColumns.push(
            revenueHTDimension,
            revenueTTCDimension,
            nbCardsDimension
          );
        }
      }
      const revenueHTDimension = groupedByPK[this.total].find(
        (x) => x.split(this.separator)[2] === calculatedColumnsEnum.REVENUE_HT
      );
      const revenueTTCDimension = groupedByPK[this.total].find(
        (x) => x.split(this.separator)[2] === calculatedColumnsEnum.REVENUE_TTC
      );
      const nbCardsDimension = groupedByPK[this.total].find(
        (x) =>
          x.split(this.separator)[2] === dimensionEnum.OMNIPLAYEVENT_NBCARDS
      );
      reorganizedColumns.push(
        revenueHTDimension,
        revenueTTCDimension,
        nbCardsDimension
      );
      return reorganizedColumns;
    },
    storeExportData(filename, subtitles) {
      const self = this;
      let datasForExport = [];

      self.tablesData.forEach((table, index) => {
        const renderer = self.$refs.tableRenderers?.[index];
        const sortKey = renderer?.sortedColumn || self.defaultSortedColumnTables;

        const data = TableTools.Helper.assembleExportDataTable(
          table.dataTable,
          table.dimensionColumns,
          table.measureColumns,
          self.groupHeaders,
          table.tableHeader,
          sortKey,
          null,
          table.totalsData,
          null,
          self.customTableClass,
          self.topGroupHeaders
        );

        datasForExport.push(data);
      });

      TableTools.Helper.storeExportData(
        datasForExport,
        self.title,
        subtitles,
        filename
      );
    },
  },
};
</script>
